/**
 * SummaryCard Component
 * Executive summary metric card with trend indicator
 *
 * Story 2.1: Executive Summary Cards
 */

import React from 'react';
import { clsx } from 'clsx';
import { TrendingUp, TrendingDown, Minus, ArrowRight } from 'lucide-react';
import { Card } from '../../common';

export type CardVariant = 'default' | 'success' | 'warning' | 'danger';

export interface SummaryCardProps {
  /** Card title/label */
  title: string;
  /** Main metric value */
  value: number | string;
  /** Optional suffix (e.g., '%', 'OKRs') */
  suffix?: string;
  /** Change from previous period */
  change?: number;
  /** Label for the change period */
  changePeriod?: string;
  /** Card color variant based on status */
  variant?: CardVariant;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Click handler for drill-down */
  onClick?: () => void;
  /** Additional class names */
  className?: string;
  /** Loading state */
  isLoading?: boolean;
}

/**
 * Get trend icon based on change value
 */
function getTrendIcon(change: number): React.ReactNode {
  if (change > 0) {
    return <TrendingUp className="w-4 h-4" />;
  } else if (change < 0) {
    return <TrendingDown className="w-4 h-4" />;
  }
  return <Minus className="w-4 h-4" />;
}

/**
 * Get trend color classes based on change value and whether higher is better
 */
function getTrendColorClasses(change: number, variant: CardVariant): string {
  // For danger variant (at-risk), positive change is bad
  const isPositiveGood = variant !== 'danger';

  if (change > 0) {
    return isPositiveGood ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50';
  } else if (change < 0) {
    return isPositiveGood ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50';
  }
  return 'text-stone-500 bg-stone-50';
}

/**
 * Get border color based on variant
 */
function getVariantClasses(variant: CardVariant): string {
  switch (variant) {
    case 'success':
      return 'border-l-4 border-l-green-500';
    case 'warning':
      return 'border-l-4 border-l-amber-500';
    case 'danger':
      return 'border-l-4 border-l-red-500';
    default:
      return 'border-l-4 border-l-blue-500';
  }
}

/**
 * Get icon background color based on variant
 */
function getIconBgClasses(variant: CardVariant): string {
  switch (variant) {
    case 'success':
      return 'bg-green-100 text-green-600';
    case 'warning':
      return 'bg-amber-100 text-amber-600';
    case 'danger':
      return 'bg-red-100 text-red-600';
    default:
      return 'bg-blue-100 text-blue-600';
  }
}

/**
 * Executive Summary Card Component
 */
export const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  suffix,
  change,
  changePeriod = 'from last period',
  variant = 'default',
  icon,
  onClick,
  className,
  isLoading = false,
}) => {
  const isClickable = !!onClick;

  if (isLoading) {
    return (
      <Card className={clsx('p-6', getVariantClasses(variant), className)}>
        <div className="animate-pulse">
          <div className="h-3 bg-stone-200 rounded w-24 mb-3" />
          <div className="h-8 bg-stone-200 rounded w-20 mb-2" />
          <div className="h-3 bg-stone-200 rounded w-16" />
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={clsx(
        'p-6 transition-all duration-200',
        getVariantClasses(variant),
        isClickable && 'cursor-pointer hover:shadow-md hover:scale-[1.02]',
        className
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Title */}
          <p className="text-sm font-medium text-stone-500 mb-1">{title}</p>

          {/* Value */}
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-stone-900">{value}</span>
            {suffix && (
              <span className="text-lg text-stone-500">{suffix}</span>
            )}
          </div>

          {/* Trend indicator */}
          {change !== undefined && (
            <div className="mt-2 flex items-center gap-2">
              <span
                className={clsx(
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                  getTrendColorClasses(change, variant)
                )}
              >
                {getTrendIcon(change)}
                {change > 0 ? '+' : ''}
                {change}%
              </span>
              <span className="text-xs text-stone-400">{changePeriod}</span>
            </div>
          )}
        </div>

        {/* Icon */}
        {icon && (
          <div
            className={clsx(
              'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center',
              getIconBgClasses(variant)
            )}
          >
            {icon}
          </div>
        )}
      </div>

      {/* Click indicator */}
      {isClickable && (
        <div className="mt-4 pt-3 border-t border-stone-100 flex items-center justify-between text-sm text-stone-500">
          <span>View details</span>
          <ArrowRight className="w-4 h-4" />
        </div>
      )}
    </Card>
  );
};

/**
 * Props for the summary cards grid
 */
export interface ExecutiveSummaryCardsProps {
  /** Total OKRs count */
  totalOKRs: number;
  /** Completion rate percentage */
  completionRate: number;
  /** Average progress percentage */
  averageProgress: number;
  /** At-risk OKRs count */
  atRiskCount: number;
  /** At-risk percentage of total */
  atRiskPercentage: number;
  /** Changes from previous period */
  changes?: {
    totalOKRs?: number;
    completionRate?: number;
    averageProgress?: number;
  };
  /** Drill-down handlers */
  onDrillDown?: {
    totalOKRs?: () => void;
    completionRate?: () => void;
    averageProgress?: () => void;
    atRisk?: () => void;
  };
  /** Loading state */
  isLoading?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * Get variant based on metric type and value
 */
function getVariantForMetric(
  metricType: 'completion' | 'progress' | 'atRisk',
  value: number
): CardVariant {
  switch (metricType) {
    case 'completion':
    case 'progress':
      if (value >= 70) return 'success';
      if (value >= 40) return 'warning';
      return 'danger';
    case 'atRisk':
      if (value <= 10) return 'success';
      if (value <= 25) return 'warning';
      return 'danger';
    default:
      return 'default';
  }
}

/**
 * Executive Summary Cards Grid
 */
export const ExecutiveSummaryCards: React.FC<ExecutiveSummaryCardsProps> = ({
  totalOKRs,
  completionRate,
  averageProgress,
  atRiskCount,
  atRiskPercentage,
  changes,
  onDrillDown,
  isLoading = false,
  className,
}) => {
  return (
    <div className={clsx('grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4', className)}>
      {/* Total OKRs */}
      <SummaryCard
        title="Total OKRs"
        value={totalOKRs}
        change={changes?.totalOKRs}
        changePeriod="from last period"
        variant="default"
        onClick={onDrillDown?.totalOKRs}
        isLoading={isLoading}
      />

      {/* Completion Rate */}
      <SummaryCard
        title="Completion Rate"
        value={completionRate}
        suffix="%"
        change={changes?.completionRate}
        changePeriod="from last period"
        variant={getVariantForMetric('completion', completionRate)}
        onClick={onDrillDown?.completionRate}
        isLoading={isLoading}
      />

      {/* Average Progress */}
      <SummaryCard
        title="Average Progress"
        value={averageProgress}
        suffix="%"
        change={changes?.averageProgress}
        changePeriod="from last period"
        variant={getVariantForMetric('progress', averageProgress)}
        onClick={onDrillDown?.averageProgress}
        isLoading={isLoading}
      />

      {/* At-Risk OKRs */}
      <SummaryCard
        title="At-Risk OKRs"
        value={atRiskCount}
        suffix={`(${atRiskPercentage}%)`}
        variant={getVariantForMetric('atRisk', atRiskPercentage)}
        onClick={onDrillDown?.atRisk}
        isLoading={isLoading}
      />
    </div>
  );
};

export default SummaryCard;
